import React, { useMemo, useRef, useState } from 'react';
import ChartTypeOption from './ChartTypeOption';
import ToggleOption from './ToggleOption';
import { ReactComponent as LineChartIcon } from '../../images/line-chart-up-01-gray.svg';
import { ReactComponent as ScatterChartIcon } from '../../images/scatter-chart-up-01.svg';
import './ChartOptions.scss';
import { setChartViewOptions, ChartViewOptions } from '../slices/AssetTrendDataSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { saveUserPerferences, DashboardConfig } from '../../dispatchers/UserPreferenceDispatcher';

interface ChartOptionsProps {
  retrieveMinMax: boolean;
  fetchCov: boolean;
  dashboardName: string;
  layout: string;
  onStandardDeviationChanged?: () => void;
  assetId?: string;
}

const ChartOptions: React.FC<ChartOptionsProps> = ({
  retrieveMinMax,
  fetchCov,
  dashboardName,
  layout,
  onStandardDeviationChanged,
  assetId,
}) => {
  const chartViewOptions = useAppSelector((state: any) => state.AssetTrendDataSlice.chartViewOptions);
  const [tooltipsEnabled, setTooltipsEnabled] = useState(chartViewOptions.showTooltip);
  const [isLogarithmic, setIsLogarithmic] = useState(chartViewOptions.isLogarithmic);
  const [standardDeviationEnabled, setStandardDeviationEnabled] = useState(chartViewOptions.showSD);
  const [eventsEnabled, setEventsEnabled] = useState(chartViewOptions.showEvents);
  const [chartType, setChartType] = useState(chartViewOptions.chartType || 'line');
  const prevSDRef = useRef(standardDeviationEnabled);
  const isTestDashboard = dashboardName?.toLowerCase().includes('tests');

  const config: DashboardConfig = {
    DashboardName: dashboardName,
    WidgetName: isTestDashboard ? 'wellTestChart' : 'timeseriesChart',
    PropertyType: 'ViewOptions',
    Preferences: isTestDashboard
      ? {
          IsLogarithmic: isLogarithmic.toString(),
          ShowTooltips: tooltipsEnabled.toString(),
        }
      : {
          LineType: chartType,
          ShowTooltips: tooltipsEnabled.toString(),
          Std: standardDeviationEnabled.toString(),
          ShowEvents: eventsEnabled.toString(),
        },
    ...((dashboardName === 'rl-well-charts' || dashboardName === 'facility-well-charts') && assetId
      ? { assetGUID: assetId }
      : {}),
  };
  const dispatch = useAppDispatch();

  useMemo(() => {
    const updateChartViewOptions = async () => {
      const chartViewOptions: ChartViewOptions = {
        chartType: chartType,
        showTooltip: tooltipsEnabled,
        showSD: standardDeviationEnabled,
        showEvents: eventsEnabled,
        isLogarithmic: isLogarithmic,
      };

      // Dispatch the action
      dispatch(
        setChartViewOptions({
          chartViewOptions,
        }),
      );

      if (prevSDRef.current !== standardDeviationEnabled) {
        prevSDRef.current = standardDeviationEnabled;
        onStandardDeviationChanged?.();
      }
      await dispatch(saveUserPerferences(config));
    };

    updateChartViewOptions();
  }, [config, tooltipsEnabled, standardDeviationEnabled, eventsEnabled, chartType]);

  const handleChartTypeClick = (chartType: string) => {
    setChartType(chartType);
  };

  return (
    <section className='chart-options' style={{ left: isTestDashboard ? '84px' : '70px' }}>
      <header className='chart-options__header'>
        <h2 className='chart-options__title'>View options</h2>
      </header>

      <div className='chart-options__content'>
        {isTestDashboard ? (
          <>
            <ToggleOption
              label='Logarithmic'
              enabled={isLogarithmic}
              onChange={(value: any) => {
                setIsLogarithmic(value);
              }}
              disabled={layout.toLowerCase() === 'split'}
            />
            <div className='chart-options__divider' />
            <ToggleOption
              label='Show tooltips'
              enabled={tooltipsEnabled}
              onChange={(value: any) => {
                setTooltipsEnabled(value);
              }}
            />
          </>
        ) : (
          <>
            <ChartTypeOption
              Icon={LineChartIcon}
              label='Line chart'
              onClick={() => {
                handleChartTypeClick('line');
              }}
            />
            <ChartTypeOption
              Icon={ScatterChartIcon}
              label='Scatter chart'
              onClick={() => handleChartTypeClick('scatter')}
            />
            <div className='chart-options__divider' />
            <ToggleOption
              label='Show tooltips'
              enabled={tooltipsEnabled}
              onChange={(value: any) => {
                setTooltipsEnabled(value);
              }}
            />
            <ToggleOption
              label='Standard deviation'
              enabled={standardDeviationEnabled}
              onChange={(value: any) => {
                setStandardDeviationEnabled(value);
              }}
              disabled={!retrieveMinMax}
            />
            <ToggleOption
              label='Show events'
              enabled={eventsEnabled}
              onChange={(value: any) => {
                setEventsEnabled(value);
              }}
              disabled={fetchCov}
            />
          </>
        )}
      </div>
    </section>
  );
};

export default ChartOptions;
